import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  Avatar,
} from '@mui/material';
import {
  Assignment,
  Add,
  Visibility,
  Refresh,
  FiberManualRecord,
  Edit,
} from '@mui/icons-material';
import { ticketService } from '../../services/ticketService';
import { Ticket } from '../../types';
import ChamadoForm from '../forms/ChamadoForm';
import ChamadosInfoView from '../modals/ChamadosInfoView';
import { toast } from 'react-toastify';

interface ClientTicketsProps {
  clientId: number;
}

const ClientTickets: React.FC<ClientTicketsProps> = ({ clientId }) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [infoViewOpen, setInfoViewOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [editTicketId, setEditTicketId] = useState<number | null>(null);
  const theme = useTheme();

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ticketService.getByClientId(clientId);

      if (response.success && response.data) {
        setTickets(response.data);
      } else {
        setError(response.message || 'Erro ao carregar chamados');
      }
    } catch (error: any) {
      console.error('Erro ao carregar chamados:', error);
      setError('Erro ao carregar chamados do cliente');
      toast.error('Erro ao carregar chamados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      loadTickets();
    }
  }, [clientId]);

  const handleFormSuccess = () => {
    loadTickets();
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'aberto':
        return 'error';
      case 'em_andamento':
        return 'warning';
      case 'resolvido':
        return 'success';
      case 'fechado':
        return 'default';
      case 'cancelado':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      aberto: 'Aberto',
      em_andamento: 'Em Andamento',
      resolvido: 'Resolvido',
      fechado: 'Fechado',
      cancelado: 'Cancelado',
    };
    return labels[status] || status;
  };

  const getPrioridadeColor = (prioridade: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (prioridade) {
      case 'baixa':
        return 'success';
      case 'normal':
        return 'info';
      case 'alta':
        return 'warning';
      case 'urgente':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPrioridadeLabel = (prioridade: string): string => {
    const labels: Record<string, string> = {
      baixa: 'Baixa',
      normal: 'Normal',
      alta: 'Alta',
      urgente: 'Urgente',
    };
    return labels[prioridade] || prioridade;
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h6" fontWeight="bold">
          Chamados do Cliente
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Atualizar">
            <IconButton
              onClick={loadTickets}
              disabled={loading}
              size="small"
            >
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setFormOpen(true)}
          >
            Novo Chamado
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Carregando chamados...
          </Typography>
        </Box>
      ) : tickets.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Assignment sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography color="text.secondary" variant="h6">
            Nenhum chamado encontrado
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Clique em "Novo Chamado" para criar o primeiro chamado
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={2}
          sx={{
            borderRadius: 2,
            overflowX: 'auto',
            mb: 1,
            border: 'none'
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: 800,
              '& .MuiTableBody-root .MuiTableCell-root': {
                py: 0.6,
                fontSize: '0.78rem'
              }
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: (t) => `color-mix(in srgb, ${theme.palette.primary.main} 12%, ${t.palette.background.paper})`,
                    fontWeight: 700,
                    fontSize: '0.73rem',
                    py: 0.8,
                    nowrap: 'nowrap',
                    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    whiteSpace: 'nowrap',
                    color: 'text.primary',
                  },
                }}
              >
                <TableCell sx={{ width: 80 }}>ID</TableCell>
                <TableCell>Assunto</TableCell>
                <TableCell sx={{ width: 100 }}>Prioridade</TableCell>
                <TableCell sx={{ width: 100 }}>Status</TableCell>
                <TableCell>Técnico</TableCell>
                <TableCell>Abertura</TableCell>
                <TableCell align="right" sx={{ width: 80 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => {
                const statusColor = getStatusColor(ticket.status);
                const prioridadeColor = getPrioridadeColor(ticket.prioridade);

                return (
                  <TableRow
                    key={ticket.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                      nowrap: 'nowrap',
                    }}
                    onClick={() => {
                      setSelectedTicketId(ticket.id);
                      setInfoViewOpen(true);
                    }}
                  >
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ fontSize: '0.78rem', color: 'primary.main', fontFamily: 'monospace' }}
                      >
                        #{ticket.id.toString().padStart(5, '0')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.82rem', lineHeight: 1.2 }}>
                        {ticket.assunto?.nome || 'Sem Assunto'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1, display: 'block', mt: 0.5 }}>
                        {ticket.descricao.substring(0, 100)}{ticket.descricao.length > 100 ? '...' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      <Chip
                        label={getPrioridadeLabel(ticket.prioridade)}
                        color={prioridadeColor as any}
                        size="small"
                        sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      <Chip
                        label={getStatusLabel(ticket.status)}
                        color={statusColor as any}
                        size="small"
                        sx={{ fontSize: '0.66rem', height: 20, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      {ticket.atribuido_para_funcionario ? (
                        <Box display="flex" alignItems="center" gap={0.8}>
                          <Avatar
                            sx={{
                              width: 24,
                              height: 24,
                              fontSize: '0.65rem',
                              fontWeight: 700,
                              bgcolor: alpha(theme.palette.secondary.main, 0.1),
                              color: 'secondary.main'
                            }}
                          >
                            {ticket.atribuido_para_funcionario.nome.charAt(0)}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                            {ticket.atribuido_para_funcionario.nome.split(' ')[0]}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
                          Não atribuído
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.6, px: 2 }}>
                      <Tooltip title={new Date(ticket.createdAt).toLocaleString()}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 500 }}>
                            {new Date(ticket.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                            {new Date(ticket.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.6, px: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <IconButton
                          size="small"
                          sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditTicketId(ticket.id);
                            setFormOpen(true);
                          }}
                        >
                          <Edit sx={{ fontSize: 18 }} color="warning" />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTicketId(ticket.id);
                            setInfoViewOpen(true);
                          }}
                        >
                          <Visibility sx={{ fontSize: 18 }} color="primary" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 0.8,
              px: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
              Mostrando <strong>{tickets.length}</strong> chamado{tickets.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </TableContainer>
      )}

      <ChamadoForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditTicketId(null);
        }}
        onSuccess={handleFormSuccess}
        clientId={clientId}
        ticketId={editTicketId}
      />

      {/* Modal de Visualização de Detalhes */}
      <ChamadosInfoView
        open={infoViewOpen}
        onClose={() => {
          setInfoViewOpen(false);
          setSelectedTicketId(null);
        }}
        ticketId={selectedTicketId}
        onSuccess={() => {
          loadTickets(); // Recarregar tickets após fechar chamado
        }}
      />
    </Box>
  );
};

export default ClientTickets; 